import { FormControl, MenuItem, Select } from "@mui/material";
import { useState } from "react";
import {
	useDataProvider,
	useNotify,
	usePermissions,
	useRecordContext,
	useRefresh,
} from "react-admin";
import type { UserRole } from "@/providers/authProvider";
import type { AdminDataProvider } from "@/providers/dataProvider";

const ROLES: { id: UserRole; name: string }[] = [
	{ id: "user", name: "User" },
	{ id: "admin", name: "Admin" },
	{ id: "super_admin", name: "Super Admin" },
];

export const RoleField = () => {
	const record = useRecordContext();
	const dataProvider = useDataProvider<AdminDataProvider>();
	const refresh = useRefresh();
	const notify = useNotify();
	const { permissions } = usePermissions<{ role: UserRole }>();
	const [loading, setLoading] = useState(false);

	if (!record) return null;

	// Only super_admin can change roles
	const canEditRole = permissions?.role === "super_admin";

	const handleChange = async (event: { target: { value: string } }) => {
		const newRole = event.target.value as UserRole;

		if (newRole === record.role) return;

		setLoading(true);
		try {
			await dataProvider.updateRole(record.id, newRole);
			notify("Role updated successfully", { type: "success" });
			refresh();
		} catch (error) {
			notify(error instanceof Error ? error.message : "Failed to update role", {
				type: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	if (!canEditRole) {
		return (
			<span>
				{ROLES.find((r) => r.id === record.role)?.name || record.role}
			</span>
		);
	}

	return (
		<FormControl size="small" sx={{ minWidth: 120 }}>
			<Select
				value={record.role}
				onChange={handleChange}
				disabled={loading}
				size="small"
			>
				{ROLES.map((role) => (
					<MenuItem key={role.id} value={role.id}>
						{role.name}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};
